import { CountryCode, countryCodes } from "@redotech/locale/country-codes";
import {
  Carriers,
  UspsServices,
} from "@redotech/redo-fulfillment-carrier-common/carrier-service-enums";
import { isLandedCostSupportedCarrierService } from "./landed-cost-supported-services";

export interface CarrierServiceLane {
  carrier: Carriers;
  service: string;
}

export type LcgUnsupportedCountries = Partial<
  Record<Carriers, ReadonlySet<CountryCode>>
>;

const dhlLcgUnsupportedCountries: ReadonlySet<CountryCode> =
  new Set<CountryCode>([
    "AF",
    "AM",
    "AN",
    "AQ",
    "AS",
    "AX",
    "AZ",
    "BV",
    "BY",
    "CC",
    "CF",
    "CK",
    "CS",
    "CU",
    "CV",
    "CX",
    "EH",
    "FK",
    "FO",
    "GE",
    "GL",
    "GS",
    "GU",
    "GW",
    "HM",
    "IM",
    "IO",
    "IQ",
    "IR",
    "KG",
    "KI",
    "KM",
    "KP",
    "KZ",
    "LS",
    "MD",
    "MF",
    "MH",
    "MK",
    "MP",
    "MR",
    "NF",
    "NR",
    "NU",
    "PF",
    "PM",
    "PN",
    "PS",
    "PW",
    "RU",
    "SB",
    "SD",
    "SH",
    "SJ",
    "SL",
    "SO",
    "SR",
    "SS",
    "ST",
    "SY",
    "TF",
    "TJ",
    "TK",
    "TM",
    "TN",
    "TO",
    "TV",
    "UA",
    "UM",
    "US",
    "UZ",
    "VA",
    "WF",
    "WS",
    "YE",
  ]);

const postalLcgUnsupportedCountries: ReadonlySet<CountryCode> =
  new Set<CountryCode>([
    "AD",
    "AE",
    "AF",
    "AG",
    "AI",
    "AL",
    "AM",
    "AN",
    "AO",
    "AQ",
    "AR",
    "AS",
    "AW",
    "AX",
    "AZ",
    "BA",
    "BB",
    "BD",
    "BF",
    "BH",
    "BI",
    "BJ",
    "BL",
    "BM",
    "BN",
    "BO",
    "BQ",
    "BR",
    "BS",
    "BT",
    "BV",
    "BW",
    "BY",
    "BZ",
    "CC",
    "CD",
    "CF",
    "CG",
    "CH",
    "CI",
    "CK",
    "CL",
    "CM",
    "CN",
    "CO",
    "CR",
    "CS",
    "CU",
    "CV",
    "CW",
    "CX",
    "DJ",
    "DM",
    "DO",
    "DZ",
    "EC",
    "EG",
    "EH",
    "ER",
    "ET",
    "FJ",
    "FK",
    "FM",
    "FO",
    "GA",
    "GD",
    "GE",
    "GF",
    "GG",
    "GH",
    "GI",
    "GL",
    "GM",
    "GN",
    "GP",
    "GQ",
    "GS",
    "GT",
    "GU",
    "GW",
    "GY",
    "HK",
    "HM",
    "HN",
    "HT",
    "ID",
    "IL",
    "IM",
    "IN",
    "IO",
    "IQ",
    "IR",
    "IS",
    "JE",
    "JM",
    "JO",
    "JP",
    "KE",
    "KG",
    "KH",
    "KI",
    "KM",
    "KN",
    "KP",
    "KR",
    "KW",
    "KY",
    "KZ",
    "LA",
    "LB",
    "LC",
    "LI",
    "LK",
    "LR",
    "LS",
    "LY",
    "MA",
    "MC",
    "MD",
    "ME",
    "MF",
    "MG",
    "MH",
    "MK",
    "ML",
    "MM",
    "MN",
    "MO",
    "MP",
    "MQ",
    "MR",
    "MS",
    "MU",
    "MV",
    "MW",
    "MX",
    "MY",
    "MZ",
    "NA",
    "NC",
    "NE",
    "NF",
    "NG",
    "NI",
    "NP",
    "NR",
    "NU",
    "OM",
    "PA",
    "PE",
    "PF",
    "PG",
    "PH",
    "PK",
    "PM",
    "PN",
    "PR",
    "PS",
    "PW",
    "PY",
    "QA",
    "RE",
    "RS",
    "RU",
    "RW",
    "SA",
    "SB",
    "SC",
    "SD",
    "SH",
    "SJ",
    "SL",
    "SM",
    "SN",
    "SO",
    "SR",
    "SS",
    "ST",
    "SV",
    "SX",
    "SY",
    "SZ",
    "TC",
    "TD",
    "TF",
    "TG",
    "TH",
    "TJ",
    "TK",
    "TL",
    "TM",
    "TN",
    "TO",
    "TR",
    "TT",
    "TV",
    "TW",
    "TZ",
    "UA",
    "UG",
    "UM",
    "UY",
    "UZ",
    "VA",
    "VC",
    "VE",
    "VG",
    "VI",
    "VN",
    "VU",
    "WF",
    "WS",
    "YE",
    "YT",
    "ZA",
    "ZM",
    "ZW",
  ]);

export const lcgUnsupportedCountries: LcgUnsupportedCountries = {
  [Carriers.FEDEX]: new Set<CountryCode>([
    "AD",
    "AF",
    "AL",
    "AM",
    "AN",
    "AO",
    "AQ",
    "AS",
    "AX",
    "AZ",
    "BG",
    "BJ",
    "BQ",
    "BV",
    "BY",
    "CC",
    "CF",
    "CI",
    "CK",
    "CS",
    "CU",
    "CX",
    "DJ",
    "EH",
    "ER",
    "FJ",
    "FK",
    "FM",
    "FO",
    "GE",
    "GF",
    "GG",
    "GI",
    "GL",
    "GQ",
    "GR",
    "GS",
    "GW",
    "HM",
    "IM",
    "IO",
    "IR",
    "IS",
    "JE",
    "KG",
    "KI",
    "KM",
    "KP",
    "KZ",
    "LA",
    "LR",
    "LS",
    "ME",
    "MH",
    "MK",
    "MM",
    "MN",
    "MP",
    "MS",
    "NC",
    "NF",
    "NR",
    "NU",
    "PF",
    "PG",
    "PM",
    "PN",
    "PS",
    "PT",
    "PW",
    "RS",
    "RU",
    "SB",
    "SD",
    "SH",
    "SI",
    "SJ",
    "SL",
    "SO",
    "SS",
    "ST",
    "SY",
    "TF",
    "TJ",
    "TK",
    "TL",
    "TM",
    "TO",
    "TV",
    "UM",
    "UZ",
    "VA",
    "VG",
    "VU",
    "WF",
    "WS",
    "YE",
    "YT",
  ]),
  [Carriers.UPS]: new Set<CountryCode>([
    "AD",
    "AF",
    "AM",
    "AN",
    "AQ",
    "AS",
    "AX",
    "AZ",
    "BT",
    "BV",
    "BY",
    "CC",
    "CF",
    "CK",
    "CS",
    "CU",
    "CX",
    "EH",
    "FJ",
    "FK",
    "FM",
    "FO",
    "GI",
    "GL",
    "GQ",
    "GS",
    "HM",
    "IM",
    "IO",
    "IR",
    "KG",
    "KH",
    "KI",
    "KP",
    "LA",
    "LY",
    "MM",
    "MP",
    "NC",
    "NR",
    "NU",
    "PF",
    "PG",
    "PM",
    "PN",
    "PR",
    "PS",
    "PW",
    "RU",
    "SB",
    "SD",
    "SH",
    "SJ",
    "SM",
    "SN",
    "SO",
    "SS",
    "ST",
    "SY",
    "TF",
    "TJ",
    "TK",
    "TL",
    "TM",
    "TN",
    "TO",
    "TV",
    "UA",
    "UM",
    "US",
    "UZ",
    "VI",
    "VU",
    "WF",
    "WS",
    "YE",
    "YT",
  ]),
  [Carriers.DHL_EXPRESS]: dhlLcgUnsupportedCountries,
  [Carriers.DHL_ECS]: dhlLcgUnsupportedCountries,
  [Carriers.APC]: new Set<CountryCode>([
    "AF",
    "AM",
    "AN",
    "AQ",
    "AS",
    "AX",
    "AZ",
    "BL",
    "BQ",
    "BV",
    "BY",
    "CC",
    "CD",
    "CF",
    "CI",
    "CK",
    "CS",
    "CU",
    "CV",
    "CW",
    "CX",
    "DZ",
    "EH",
    "FK",
    "FM",
    "FO",
    "GD",
    "GE",
    "GL",
    "GS",
    "GU",
    "GW",
    "HM",
    "HN",
    "IM",
    "IO",
    "IQ",
    "IR",
    "KG",
    "KI",
    "KM",
    "KN",
    "KP",
    "KZ",
    "LC",
    "LS",
    "LY",
    "MD",
    "MF",
    "MH",
    "MK",
    "MP",
    "MR",
    "MS",
    "NF",
    "NR",
    "NU",
    "PF",
    "PM",
    "PN",
    "PS",
    "PW",
    "RU",
    "SB",
    "SH",
    "SJ",
    "SL",
    "SM",
    "SO",
    "SR",
    "SS",
    "ST",
    "SX",
    "SY",
    "TC",
    "TF",
    "TJ",
    "TK",
    "TL",
    "TM",
    "TN",
    "TO",
    "TV",
    "UA",
    "UM",
    "US",
    "UZ",
    "VA",
    "VC",
    "VG",
    "VU",
    "WF",
    "WS",
    "YE",
    "YT",
  ]),
  [Carriers.USPS]: postalLcgUnsupportedCountries,
  [Carriers.AUSTRALIA_POST]: postalLcgUnsupportedCountries,
  [Carriers.EPOST_GLOBAL]: new Set<CountryCode>(["IS"]),
};

const domesticUspsServices: ReadonlySet<string> = new Set([
  UspsServices.Priority,
  UspsServices.Express,
]);

export function isInternationalLcgLane(lane: CarrierServiceLane): boolean {
  if (
    lane.carrier === Carriers.USPS &&
    domesticUspsServices.has(lane.service)
  ) {
    return false;
  }
  return isLandedCostSupportedCarrierService(lane.carrier, lane.service);
}

export function canLaneShipLcgTo(
  lane: CarrierServiceLane,
  countryCode: CountryCode,
  unsupportedCountries: LcgUnsupportedCountries = lcgUnsupportedCountries,
): boolean {
  return (
    isInternationalLcgLane(lane) &&
    !unsupportedCountries[lane.carrier]?.has(countryCode)
  );
}

export function canShipLcgTo(
  enabledLanes: readonly CarrierServiceLane[],
  countryCode: CountryCode,
  unsupportedCountries: LcgUnsupportedCountries = lcgUnsupportedCountries,
): boolean {
  return enabledLanes.some((lane) =>
    canLaneShipLcgTo(lane, countryCode, unsupportedCountries),
  );
}

export function lcgShippableCountries(
  enabledLanes: readonly CarrierServiceLane[],
  unsupportedCountries: LcgUnsupportedCountries = lcgUnsupportedCountries,
): CountryCode[] {
  return countryCodes.filter((countryCode) =>
    canShipLcgTo(enabledLanes, countryCode, unsupportedCountries),
  );
}
